import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { RotateCcw, PlayCircle, CheckCircle2, XCircle } from "lucide-react";

import styles from "./aiSettings.module.scss";
import Select, { AsyncSelect } from "../../components/select";
import { Can, usePermissions } from "../../context/permissionContext";
import { saveOverviewDraft } from "../summary/overviewDraft";
import { formatMongoDBTime } from "../../utills/dateAndTime";
import AddedBy from "../../components/addedBy";
import RunCard from "./RunCard";
import { isOverviewTranslationRun, isTranslationTask, testsOnBook } from "./taskKinds";

const API = process.env.REACT_APP_API_ROOT;

/** How long to wait after typing an OpenRouter id before checking it. */
const LOOKUP_DELAY = 500;

const formFrom = (task) => {
  const active = task.active || {};
  const defaults = task.defaults || {};
  return {
    provider: active.provider || defaults.provider || "openai",
    model: active.model || defaults.model || "",
    maxOutputTokens: active.maxOutputTokens ?? defaults.maxOutputTokens ?? "",
    temperature: active.temperature ?? "",
    reasoningEffort: active.reasoningEffort || defaults.reasoningEffort || "",
    note: "",
    // The shipped prompt rather than an empty box: an empty prompt is not "no
    // change", it is a different prompt.
    systemPrompt: active.systemPrompt || defaults.systemPrompt || "",
    userPromptTemplate: active.userPromptTemplate || defaults.userPromptTemplate || "",
  };
};

/**
 * One AI task's settings: service, model, reasoning effort, token budget,
 * prompts, a test run, and the task's own version history.
 *
 * OpenAI offers a short curated list, so its model is a dropdown. OpenRouter
 * has several hundred, so its model is TYPED — and checked against
 * OpenRouter's catalogue as you type, which is also how the page learns
 * whether that model takes a reasoning effort.
 */
const TaskSettings = ({ task, options, headers, onSaved }) => {
  const { can } = usePermissions();
  const [form, setForm] = useState(() => formFrom(task));
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);

  // The last passing test, and what it tested. Cleared whenever an input it
  // covered changes, so it can never vouch for settings it never saw.
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testBook, setTestBook] = useState(null);
  const [language, setLanguage] = useState("hi");
  const [sampleText, setSampleText] = useState("");
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  // "Also put this test's Overview on the summary" — ticked by default, and
  // named after the book beside Save, because what it writes goes live.
  const [applyToSummary, setApplyToSummary] = useState(true);

  // A typed OpenRouter id, looked up: idle | checking | found | missing.
  const [lookup, setLookup] = useState({ state: "idle", model: null });

  const isOpenRouter = form.provider === "openrouter";
  const selectedModel = isOpenRouter
    ? lookup.state === "found"
      ? lookup.model
      : null
    : options.openaiModels.find((m) => m.id === form.model) || null;
  const selectedProvider = options.providers.find((p) => p.id === form.provider) || null;

  const loadVersions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/ai-config/versions`, {
        headers,
        params: { key: task.key },
      });
      setVersions(data?.data || []);
    } catch (error) {
      console.error("AI settings versions error::", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.key, headers.Authorization]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Re-seed when the task's in-force settings change (after a save or restore).
  useEffect(() => {
    setForm(formFrom(task));
    setTestResult(null);
  }, [task]);

  // Check a typed OpenRouter id once typing pauses.
  useEffect(() => {
    if (!isOpenRouter) return undefined;
    const id = form.model.trim();
    if (!id) {
      setLookup({ state: "idle", model: null });
      return undefined;
    }
    setLookup({ state: "checking", model: null });
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/ai-config/openrouter-model`, { headers, params: { id } });
        setLookup(data?.data?.found ? { state: "found", model: data.data } : { state: "missing", model: null });
      } catch {
        setLookup({ state: "missing", model: null });
      }
    }, LOOKUP_DELAY);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenRouter, form.model]);

  // Anything the test fingerprint covers invalidates the last test.
  const setCovered = (patch) => {
    setTestResult(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  // Only the dial this model takes is sent — the server rejects the other one
  // rather than ignoring it, which keeps the page honest about what is in force.
  const payload = () => ({
    key: task.key,
    provider: form.provider,
    model: form.model.trim(),
    maxOutputTokens: form.maxOutputTokens ? Number(form.maxOutputTokens) : undefined,
    systemPrompt: form.systemPrompt || undefined,
    userPromptTemplate: task.usesUserPrompt ? form.userPromptTemplate || undefined : undefined,
    ...(selectedModel?.reasoning
      ? { reasoningEffort: form.reasoningEffort || undefined }
      : { temperature: form.temperature === "" ? undefined : Number(form.temperature) }),
  });

  const ready = () => {
    if (!form.model.trim()) {
      toast.error("Choose a model first.");
      return false;
    }
    if (isOpenRouter && lookup.state !== "found") {
      toast.error("That OpenRouter model id isn't confirmed yet — check it against openrouter.ai/models.");
      return false;
    }
    return true;
  };

  // "Open in summary": the book's summary editor in a NEW tab (so this page
  // keeps its runs) with the run's Overview filled in. It saves nothing.
  const canOpenInSummary = (run) =>
    can("summaries", "edit") && run?.key === "overviewGeneration" && run.parsed > 0 && Boolean(run.summaryId);

  const openInSummary = (run) => {
    const draft = saveOverviewDraft({
      summaryId: run.summaryId,
      fields: run.fields,
      model: run.model,
      bookTitle: run.bookTitle,
    });
    // Opening without it would show the SAVED text as if it were the run's —
    // so don't open at all.
    if (!draft) {
      toast.error("This browser blocked storage, so the run can't be handed to the summary editor.");
      return;
    }
    window.open(`/summary/edit/${run.summaryId}?overviewDraft=${draft}`, "_blank");
  };

  const handleTest = async () => {
    if (!ready()) return;
    const needsBook = testsOnBook(task.key);
    if (needsBook && !testBook) return toast.error("Pick a book to test against.");

    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/ai-config/test`,
        {
          ...payload(),
          ...(needsBook
            ? { bookId: testBook.value, ...(isTranslationTask(task.key) ? { language } : {}) }
            : { language, sampleText: sampleText || undefined }),
        },
        { headers }
      );
      if (data?.status) {
        setTestResult({ ...data.data, message: data.message });
        setRuns((previous) =>
          [{ ...data.data, id: `${Date.now()}`, at: new Date(), settings: { ...form } }, ...previous].slice(0, 10)
        );
        if (data.data.parsed > 0) toast.success(data.message);
        else toast.error("The test produced nothing usable — this can't be activated.");
      }
    } catch (error) {
      console.error("AI settings test error::", error);
      toast.error(error.response?.data?.error || "The test failed.");
    } finally {
      setTesting(false);
    }
  };

  const loadBooks = async (input) => {
    if (!input || input.length < 2) return [];
    try {
      // The title search, not /books/getall: getall answers with
      // `allBooksDocs` and ignores a `bookName` filter, so this list was
      // always empty. includeSummarized: a test can run against any book.
      const { data } = await axios.get(
        `${API}/api/admin/books/getall/booknames/${encodeURIComponent(input.trim())}?includeSummarized=1`,
        { headers }
      );
      return (data?.data || []).map((b) => ({
        value: b.bookId,
        label: b.authorName ? `${b.bookName} — ${b.authorName}` : b.bookName,
      }));
    } catch (error) {
      console.error("book lookup error::", error);
      return [];
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!ready()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/ai-config/save`,
        { ...payload(), note: form.note || undefined, testResult: testResult || undefined },
        { headers }
      );
      if (data?.status) {
        toast.success(data.message || "Saved.");
        // The settings are saved whatever happens next; writing the run's
        // Overview is its own request and reports on its own.
        if (applyToSummary && canOpenInSummary(testResult)) await writeOverviewToSummary(testResult);
        if (applyToSummary && canWriteTranslation(testResult)) await writeTranslationToSummary(testResult);
        if (applyToSummary && canWriteAbout(testResult)) await writeAboutToBook(testResult);
        setTestResult(null);
        await Promise.all([onSaved(), loadVersions()]);
      }
    } catch (error) {
      console.error("AI settings save error::", error);
      toast.error(error.response?.data?.error || "Could not save the settings.");
    } finally {
      setSaving(false);
    }
  };

  // Writes the passing test's Overview onto the book's summary (English only;
  // About the Book lands on the book, as a normal summary save does). Live in
  // the app straight away — that is the point, and why the box says so.
  const writeOverviewToSummary = async (run) => {
    try {
      const { data } = await axios.put(
        `${API}/api/admin/summary/apply-overview/${run.summaryId}`,
        { fields: run.fields },
        { headers }
      );
      if (data?.status) toast.success(`Overview written to the “${run.bookTitle}” summary (English).`);
    } catch (error) {
      console.error("Apply overview error::", error);
      toast.error(
        `Settings saved, but the Overview couldn't be written to “${run.bookTitle}”: ${
          error.response?.data?.error || "the request failed"
        }`
      );
    }
  };

  // Overview translation: the passing test's sections, onto the summary in the
  // tested language (About the Book onto the book). Live in the app.
  const canWriteTranslation = (run) =>
    can("summaries", "edit") && isOverviewTranslationRun(run) && run.parsed > 0 && Boolean(run.summaryId);

  // About the Book translation: the passing test's Book Name, Author Name and
  // About the Book onto the BOOK in the tested language. Live in the app.
  const canWriteAbout = (run) =>
    can("books", "edit") && run?.key === "aboutTheBookTranslation" && run.parsed > 0 && Boolean(run.bookId);

  const languageName = (code) => options.languages.find((l) => l.code === code)?.name || code;

  const writeTranslationToSummary = async (run) => {
    try {
      const { data } = await axios.put(
        `${API}/api/admin/summary/apply-overview-translation/${run.summaryId}`,
        // A review run also records how its review went on the summary.
        { language: run.language, fields: run.fields, ...(run.review ? { review: run.review } : {}) },
        { headers }
      );
      if (data?.status) toast.success(data.message || `Overview written to “${run.bookTitle}” in ${languageName(run.language)}.`);
    } catch (error) {
      console.error("Apply overview translation error::", error);
      toast.error(
        `Settings saved, but the translation couldn't be written to “${run.bookTitle}”: ${
          error.response?.data?.error || "the request failed"
        }`
      );
    }
  };

  const writeAboutToBook = async (run) => {
    try {
      const { data } = await axios.put(
        `${API}/api/admin/books/apply-about-translation/${run.bookId}`,
        { language: run.language, names: run.names, translated: run.translated },
        { headers }
      );
      if (data?.status) toast.success(data.message || `Written to “${run.bookTitle}” in ${languageName(run.language)}.`);
    } catch (error) {
      console.error("Apply About the Book translation error::", error);
      toast.error(
        `Settings saved, but “${run.bookTitle}” couldn't be updated: ${error.response?.data?.error || "the request failed"}`
      );
    }
  };

  const handleRestore = async (version) => {
    try {
      const { data } = await axios.post(`${API}/api/admin/ai-config/restore/${version._id}`, {}, { headers });
      if (data?.status) {
        toast.success(data.message || "Restored.");
        await Promise.all([onSaved(), loadVersions()]);
      }
    } catch (error) {
      console.error("AI settings restore error::", error);
      toast.error(error.response?.data?.error || "Could not restore that version.");
    }
  };

  const providerOptions = options.providers.map((p) => ({
    value: p.id,
    label: p.configured ? p.label : `${p.label} — no key on this server`,
  }));
  const modelOptions = options.openaiModels.map((m) => ({ value: m.id, label: m.label }));
  // The API's own names, with the two extremes spelled out — "xhigh" and
  // "max" mean nothing to someone choosing a setting for the first time.
  const EFFORT_LABELS = {
    none: "none — no thinking, fastest",
    minimal: "minimal",
    low: "low",
    medium: "medium",
    high: "high",
    xhigh: "xhigh — extra high",
    max: "max — the most, and the costliest",
  };
  // Narrowed to what THIS model takes where the catalogue knows — support is
  // per-model and the API answers an unsupported level with a 400. A model
  // that has not said offers the whole range.
  const effortOptions = (selectedModel?.efforts || options.reasoningEfforts).map((e) => ({
    value: e,
    label: EFFORT_LABELS[e] || e,
  }));
  const languageOptions = options.languages.map((l) => ({ value: l.code, label: l.name }));
  const active = task.active || {};
  // A task with nothing saved can run on another task's settings (Overview
  // translation runs on Translation's until it gets its own).
  const inheritedLabel =
    (options?.tasks || []).find((t) => t.key === active.inheritedFrom)?.label || active.inheritedFrom;

  return (
    <>
      <div className={styles.panel}>
        <p className={styles.subtitle}>{task.description}</p>
        <div className={styles.currentRow}>
          <span className={styles.currentLabel}>In force now</span>
          <span className={styles.currentValue}>
            {active.provider === "openrouter" ? "OpenRouter · " : "OpenAI · "}
            {active.model}
            {active.reasoningEffort ? ` · ${active.reasoningEffort} reasoning` : ""}
            <em className={styles.fromEnv}>
              {active.inheritedFrom
                ? ` — the ${inheritedLabel} settings${active.inheritedVersion ? ` (version ${active.inheritedVersion})` : ""}, until this is saved`
                : active.source === "env"
                  ? " — the default, nothing saved yet"
                  : ` — version ${active.version}`}
            </em>
          </span>
        </div>

        <form onSubmit={handleSave}>
          <div className={styles.grid}>
            <div>
              <label className="label">Service</label>
              <Select
                options={providerOptions}
                value={providerOptions.find((o) => o.value === form.provider) || null}
                // Switching service clears the model: an id from one is never
                // valid on the other (`gpt-5.5` vs `openai/gpt-5.5`).
                onChange={(option) => setCovered({ provider: option?.value || "openai", model: "" })}
              />
              {selectedProvider && !selectedProvider.configured && (
                <p className={styles.hint}>
                  This server has no {selectedProvider.envKey}. Runs will fail until it is set.
                </p>
              )}
            </div>

            <div>
              <label className="label">Model</label>
              {isOpenRouter ? (
                <>
                  <input
                    type="text"
                    className="form_control"
                    value={form.model}
                    spellCheck={false}
                    placeholder="e.g. openai/gpt-5.6-luna"
                    onChange={(e) => setCovered({ model: e.target.value })}
                  />
                  <p className={styles.hint}>
                    {lookup.state === "checking" && "Checking…"}
                    {lookup.state === "missing" && (
                      <span className={styles.lookupBad}>
                        <XCircle size={13} /> Not an OpenRouter model id — copy it from{" "}
                        <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer">
                          openrouter.ai/models
                        </a>
                        .
                      </span>
                    )}
                    {lookup.state === "found" && (
                      <span className={styles.lookupGood}>
                        <CheckCircle2 size={13} /> {lookup.model.label}
                        {lookup.model.pricePerMillionOut
                          ? ` · $${lookup.model.pricePerMillionIn}/M in, $${lookup.model.pricePerMillionOut}/M out`
                          : lookup.model.free
                            ? " · free"
                            : ""}
                      </span>
                    )}
                    {lookup.state === "idle" && (
                      <>
                        Type the id as OpenRouter lists it —{" "}
                        <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer">
                          browse models
                        </a>
                        .
                      </>
                    )}
                  </p>
                </>
              ) : (
                <Select
                  options={modelOptions}
                  value={modelOptions.find((o) => o.value === form.model) || null}
                  onChange={(option) => setCovered({ model: option?.value || "" })}
                />
              )}
              {selectedModel && (
                <p className={styles.hint}>
                  Up to {Number(selectedModel.maxOutputTokens).toLocaleString()} output tokens.{" "}
                  {selectedModel.reasoning
                    ? "A reasoning model — takes a reasoning effort, no temperature."
                    : "Takes a temperature, no reasoning effort."}
                </p>
              )}
            </div>

            <div>
              <label className="label">Max output tokens</label>
              <input
                type="number"
                className="form_control"
                min={256}
                max={selectedModel?.maxOutputTokens}
                value={form.maxOutputTokens}
                onChange={(e) => setForm((f) => ({ ...f, maxOutputTokens: e.target.value }))}
                placeholder={String(task.defaults?.maxOutputTokens || "")}
              />
              <p className={styles.hint}>Output is cut off mid-way if this is too low.</p>
            </div>

            {/* Only the dial this model accepts is shown — offering both would
                just produce rejected saves. */}
            {selectedModel?.reasoning ? (
              <div>
                <label className="label">Reasoning effort</label>
                <Select
                  options={effortOptions}
                  value={effortOptions.find((o) => o.value === form.reasoningEffort) || null}
                  placeholder={
                    form.reasoningEffort
                      ? `${form.reasoningEffort} — not available on this model`
                      : "Select..."
                  }
                  onChange={(option) => setForm((f) => ({ ...f, reasoningEffort: option?.value || "" }))}
                />
                <p className={styles.hint}>Higher thinks longer before writing — better, slower, costlier. Not every model takes every level; test a run before saving.</p>
              </div>
            ) : selectedModel ? (
              <div>
                <label className="label">Temperature</label>
                <input
                  type="number"
                  className="form_control"
                  step="0.1"
                  min={0}
                  max={2}
                  value={form.temperature}
                  onChange={(e) => setForm((f) => ({ ...f, temperature: e.target.value }))}
                  placeholder={String(task.defaults?.temperature ?? "")}
                />
                <p className={styles.hint}>Lower is more literal, higher more varied.</p>
              </div>
            ) : (
              <div />
            )}

            <div className={styles.wide}>
              <label className="label">Note (optional)</label>
              <input
                type="text"
                className="form_control"
                value={form.note}
                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Why this change"
              />
            </div>
          </div>

          <div className={styles.promptBlock}>
            <label className="label">{task.usesUserPrompt ? "System prompt" : "Translation prompt"}</label>
            <p className={styles.hint}>{task.placeholders?.systemPrompt}</p>
            <textarea
              className="form_control"
              rows={14}
              value={form.systemPrompt}
              onChange={(e) => setCovered({ systemPrompt: e.target.value })}
            />
            {form.systemPrompt !== task.defaults?.systemPrompt && (
              <button
                type="button"
                className={styles.resetLink}
                onClick={() => setCovered({ systemPrompt: task.defaults?.systemPrompt || "" })}
              >
                Reset to the shipped prompt
              </button>
            )}
          </div>

          {task.usesUserPrompt && (
            <div className={styles.promptBlock}>
              <label className="label">User prompt</label>
              <p className={styles.hint}>Sent with each book. {task.placeholders?.userPromptTemplate}</p>
              <textarea
                className="form_control"
                rows={5}
                value={form.userPromptTemplate}
                onChange={(e) => setCovered({ userPromptTemplate: e.target.value })}
              />
              {form.userPromptTemplate !== task.defaults?.userPromptTemplate && (
                <button
                  type="button"
                  className={styles.resetLink}
                  onClick={() => setCovered({ userPromptTemplate: task.defaults?.userPromptTemplate || "" })}
                >
                  Reset to the shipped prompt
                </button>
              )}
            </div>
          )}

          {/* A prompt or model change can't go live without running once and
              checking what came back. Reading well is not evidence. */}
          <div className={styles.testBlock}>
            <div className={styles.testRow}>
              {isTranslationTask(task.key) && (
                <div className={styles.testLanguage}>
                  <label className="label">Test language</label>
                  <Select
                    options={languageOptions}
                    value={languageOptions.find((o) => o.value === language) || null}
                    onChange={(option) => setLanguage(option?.value || "hi")}
                  />
                </div>
              )}
              {/* Translation tests a sample text; every other task — Overview
                  translation included — tests against a real book. */}
              {!testsOnBook(task.key) ? (
                <div className={styles.testPicker}>
                  <label className="label">Text to translate (optional)</label>
                  <input
                    type="text"
                    className="form_control"
                    value={sampleText}
                    onChange={(e) => setSampleText(e.target.value)}
                    placeholder="Leave empty for a short sample paragraph with HTML"
                  />
                </div>
              ) : (
                <div className={styles.testPicker}>
                  <label className="label">Test against a book</label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={loadBooks}
                    value={testBook}
                    onChange={setTestBook}
                    placeholder="Type a book title…"
                  />
                </div>
              )}
              <Can feature="aiSettings" action="edit">
                <button type="button" className="btn btn-outline-secondary" onClick={handleTest} disabled={testing}>
                  <PlayCircle size={16} /> {testing ? "Running…" : "Run test"}
                </button>
              </Can>
            </div>

            {testResult && (
              <div className={testResult.parsed > 0 ? styles.testPass : styles.testFail}>
                <strong>{testResult.parsed > 0 ? "Test passed" : "Test failed"}</strong> — {testResult.message || ""}
                {testResult.bookTitle ? ` (“${testResult.bookTitle}”)` : ""}
                {testResult.parsed > 0 && " You can now save these settings."}
                {/* Here as well as on the run card: the card's copy only shows
                    once a run is expanded, and that is easy to miss. */}
                {canOpenInSummary(testResult) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => openInSummary(testResult)}
                  >
                    Open in “{testResult.bookTitle}” summary
                  </button>
                )}
              </div>
            )}

            {runs.length > 0 && (
              <div className={styles.runList}>
                {runs.map((run) => (
                  <RunCard
                    key={run.id}
                    run={run}
                    isOpen={openRun === run.id}
                    onToggle={() => setOpenRun(openRun === run.id ? null : run.id)}
                    onReuse={() => {
                      setForm((previous) => ({ ...previous, ...run.settings }));
                      toast.success("Loaded that run's settings into the form.");
                    }}
                    // A new tab, so this page keeps its runs. The editor fills
                    // the Overview from the run and saves nothing on its own.
                    onOpenInSummary={canOpenInSummary(run) ? () => openInSummary(run) : null}
                  />
                ))}
              </div>
            )}
          </div>

          <Can feature="aiSettings" action="edit">
            <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
              <button type="submit" className="btn btn-dark" disabled={saving}>
                {saving ? "Saving…" : "Save and make active"}
              </button>
              {canOpenInSummary(testResult) && (
                <label className="d-flex align-items-center gap-2 mb-0">
                  <input
                    type="checkbox"
                    checked={applyToSummary}
                    onChange={(event) => setApplyToSummary(event.target.checked)}
                  />
                  Also put this test's Overview on the “{testResult.bookTitle}” summary (English, live in the app)
                </label>
              )}
              {canWriteTranslation(testResult) && (
                <label className="d-flex align-items-center gap-2 mb-0">
                  <input
                    type="checkbox"
                    checked={applyToSummary}
                    onChange={(event) => setApplyToSummary(event.target.checked)}
                  />
                  Also put this test's translation on the “{testResult.bookTitle}” summary (
                  {languageName(testResult.language)}, live in the app)
                </label>
              )}
              {canWriteAbout(testResult) && (
                <label className="d-flex align-items-center gap-2 mb-0">
                  <input
                    type="checkbox"
                    checked={applyToSummary}
                    onChange={(event) => setApplyToSummary(event.target.checked)}
                  />
                  Also put this test's Book Name, Author and About the Book on “{testResult.bookTitle}” (
                  {languageName(testResult.language)}, live in the app)
                </label>
              )}
            </div>
          </Can>
        </form>
      </div>

      <div className="table_scrollbar table-responsive mt-3">
        <table className="table">
          <thead>
            <tr>
              <th className="index">Version</th>
              <th className="min_width">Status</th>
              <th className="min_width">Service</th>
              <th className="mid_width">Model</th>
              <th className="min_width">Tokens</th>
              <th className="min_width">Quality dial</th>
              <th className="max_width">Note</th>
              <th className="min_width">Saved on</th>
              <th className="min_width">Saved by</th>
              <th className="action">Action</th>
            </tr>
          </thead>
          <tbody>
            {versions.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center text-muted py-4">
                  Nothing saved yet — {task.label.toLowerCase()} runs on its defaults.
                </td>
              </tr>
            ) : (
              versions.map((version) => (
                <tr key={version._id}>
                  <td className="index">{version.version}</td>
                  <td className="min_width">
                    <span className={version.status === "active" ? styles.statusActive : styles.statusOther}>
                      {version.status}
                    </span>
                  </td>
                  <td className="min_width">{version.provider === "openrouter" ? "OpenRouter" : "OpenAI"}</td>
                  <td className="mid_width">{version.model}</td>
                  <td className="min_width">{version.maxOutputTokens?.toLocaleString() || "—"}</td>
                  <td className="min_width">
                    {version.reasoningEffort || (typeof version.temperature === "number" ? version.temperature : "—")}
                  </td>
                  <td className="max_width">{version.note || "—"}</td>
                  <td className="min_width">{formatMongoDBTime(version.createdAt)}</td>
                  <td className="min_width">
                    <AddedBy record={version} />
                  </td>
                  <td className="action">
                    {version.status !== "active" && (
                      <Can feature="aiSettings" action="edit">
                        <div title="Restore this version" onClick={() => handleRestore(version)}>
                          <RotateCcw size={18} />
                        </div>
                      </Can>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TaskSettings;
